import { createClient } from '@supabase/supabase-js';
import { ref } from 'vue';

// Supabase configuration
const supabaseUrl = process.env.VUE_APP_SUPABASE_URL || 'https://obchfdkehwfbicjxlxpu.supabase.co';
const supabaseAnonKey = process.env.VUE_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9iY2hmZGtlaHdmYmljanhseHB1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAyMjE0MzAsImV4cCI6MjA2NTc5NzQzMH0.uf-NG2zM6kPbTEnnSzeiwKjSmLataZYbc7uuNDDDJdc';

// Create Supabase client with extended session configuration
const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    storageKey: 'supabase.auth.token',
    flowType: 'pkce'
  }
});

// Authentication service
export const authService = {
  // Send OTP to email (for login - existing users)
  async sendOTP(email) {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true, // Allow new users to sign up
        emailRedirectTo: `${window.location.origin}/verify-otp`,
      },
    });

    if (error) throw error;
    return data;
  },


  // Verify OTP and sign in
  async verifyOTP(email, token) {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (error) throw error;

    // Store session data
    if (data.session) {
      localStorage.setItem("access_token", data.session.access_token);
      localStorage.setItem("refresh_token", data.session.refresh_token);
      localStorage.setItem("user", JSON.stringify(data.user));
    }

    return data;
  },

  // Password sign-in. Only accounts given a password by an admin (such as
  // the ChatGPT and Claude directory reviewer account) have one.
  async signInWithPassword(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) throw error;

    if (data.session) {
      localStorage.setItem("access_token", data.session.access_token);
      localStorage.setItem("refresh_token", data.session.refresh_token);
      localStorage.setItem("user", JSON.stringify(data.user));
    }

    return data;
  },

  // Sign out
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error("Signout error:", error);
    } finally {
      // Always clear local storage
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
    }
  },

  // Get current user from Supabase
  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) throw error;
    
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    }
    
    return user;
  },

  // Get current session
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  // Refresh session
  async refreshSession() {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) throw error;
    
    if (data.session) {
      localStorage.setItem("access_token", data.session.access_token);
      localStorage.setItem("refresh_token", data.session.refresh_token);
      localStorage.setItem("user", JSON.stringify(data.user));
    }
    
    return data;
  },

  // Update user profile
  async updateProfile(updates) {
    const { data, error } = await supabase.auth.updateUser(updates);
    
    if (error) throw error;
    
    if (data.user) {
      localStorage.setItem("user", JSON.stringify(data.user));
    }
    
    return data.user;
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem("access_token");
  },

  // Get stored token
  getToken() {
    return localStorage.getItem("access_token");
  },

  // Get stored user
  getStoredUser() {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  },

  // Check if user is admin (you can customize this logic)
  isAdmin() {
    const user = this.getStoredUser();
    return user?.user_metadata?.role === "admin" || user?.email === "admin@example.com";
  },

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  },

  // Sign in with Google OAuth
  async signInWithGoogle(redirectTo = null) {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo || `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) throw error;
    return data;
  },

  async getOAuthAuthorizationDetails(authorizationId) {
    const { data, error } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
    if (error) throw error;
    return data;
  },

  async decideOAuthAuthorization(authorizationId, decision) {
    const method = decision === 'approve'
      ? supabase.auth.oauth.approveAuthorization.bind(supabase.auth.oauth)
      : supabase.auth.oauth.denyAuthorization.bind(supabase.auth.oauth);
    const { data, error } = await method(authorizationId, { skipBrowserRedirect: true });
    if (error) throw error;
    return data;
  },
};

// Auth state management
export class AuthManager {
  constructor() {
    // Optimistic auth: restore user from localStorage so isAuthenticated
    // returns true immediately for returning users, before the async
    // Supabase session check resolves (~10s delay).
    const storedToken = localStorage.getItem("access_token");
    const storedUser = localStorage.getItem("user");
    let initialUser = null;
    if (storedToken && storedUser) {
      try {
        initialUser = JSON.parse(storedUser);
      } catch (e) {
        // ignore
      }
    }

    this._user = ref(initialUser);
    this._loading = ref(true);
    this.listeners = [];
    this.lastNotificationSignature = null;

    this.init();
  }

  get user() {
    return this._user.value;
  }

  get loading() {
    return this._loading.value;
  }

  async init() {
    try {
      // Set up auth state listener
      authService.onAuthStateChange((event, session) => {
        console.log('Auth state changed:', event, session);
        
        if (session?.user) {
          this.setUser(session.user);
          // Store tokens for API calls
          if (session.access_token) {
            localStorage.setItem("access_token", session.access_token);
          }
          if (session.refresh_token) {
            localStorage.setItem("refresh_token", session.refresh_token);
          }
        } else {
          this.setUser(null);
          // Clear tokens
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
        }
        
        this._loading.value = false;
        this.notifyListeners();
      });

      // Get initial session
      const session = await authService.getSession();
      if (session?.user) {
        this.setUser(session.user);
        // Store tokens for API calls
        if (session.access_token) {
          localStorage.setItem("access_token", session.access_token);
        }
        if (session.refresh_token) {
          localStorage.setItem("refresh_token", session.refresh_token);
        }
      } else {
        this.setUser(null);
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
      }

    } catch (error) {
      console.error("Auth init error:", error);
      this.setUser(null);
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
    } finally {
      this._loading.value = false;
      this.notifyListeners();
    }
  }

  setUser(user) {
    this._user.value = user;
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    }
  }

  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== callback
      );
    };
  }

  notifyListeners({ force = false } = {}) {
    const signature = JSON.stringify({
      loading: this.loading,
      userId: this.user?.id || null,
      email: this.user?.email || null,
      updatedAt: this.user?.updated_at || null,
    });

    if (!force && signature === this.lastNotificationSignature) return;
    this.lastNotificationSignature = signature;

    this.listeners.forEach((callback) => {
      callback({
        user: this.user,
        loading: this.loading,
        isAuthenticated: !!this.user,
        isAdmin: this.user?.user_metadata?.role === "admin" || this.user?.email === "admin@example.com",
      });
    });
  }

  get isAuthenticated() {
    return !!this.user;
  }

  get isAdmin() {
    return this.user?.user_metadata?.role === "admin" || this.user?.email === "admin@example.com";
  }

  get isLoading() {
    return this.loading;
  }

  getToken() {
    return localStorage.getItem("access_token");
  }

  async sendOTP(email) {
    const result = await authService.sendOTP(email);
    return result;
  }


  async verifyOTP(email, token) {
    const result = await authService.verifyOTP(email, token);
    this.setUser(result.user);
    this.notifyListeners();
    return result;
  }

  async signInWithPassword(email, password) {
    const result = await authService.signInWithPassword(email, password);
    this.setUser(result.user);
    this.notifyListeners();
    return result;
  }

  async signOut() {
    await authService.signOut();
    this.setUser(null);
    this.notifyListeners();
  }

  async updateProfile(updates) {
    const user = await authService.updateProfile(updates);
    this.setUser(user);
    this.notifyListeners({ force: true });
    return user;
  }

  async refreshSession() {
    try {
      const result = await authService.refreshSession();
      if (result.session?.user) {
        this.setUser(result.session.user);
        this.notifyListeners();
      }
      return result;
    } catch (error) {
      console.error("Session refresh failed:", error);
      // If refresh fails, sign out
      this.setUser(null);
      this.notifyListeners();
      throw error;
    }
  }

  async signInWithGoogle(redirectTo = null) {
    const result = await authService.signInWithGoogle(redirectTo);
    return result;
  }

  async getOAuthAuthorizationDetails(authorizationId) {
    return authService.getOAuthAuthorizationDetails(authorizationId);
  }

  async decideOAuthAuthorization(authorizationId, decision) {
    return authService.decideOAuthAuthorization(authorizationId, decision);
  }
}

// Create global auth manager instance
export const authManager = new AuthManager();
